import { type PropsWithChildren, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
	Dialog,
	DialogContent,
	DialogTrigger,
} from '@cocoonspace/ui/components/dialog'
import { Form } from '@cocoonspace/ui/components/form'
import { zodResolver } from '@hookform/resolvers/zod'

import { type LocationSchema, locationSchema } from '../../event-type-form'
import { NextBtn } from './next-btn'
import { PrevBtn } from './prev-btn'
import { StepId, StepProvider } from './provider'
import { StepCalendarSync } from './step-calendar-sync'
import { StepLocationType } from './step-location-type'
import { StepPriority } from './step-priority'
import { StepSpaces } from './step-spaces'
import { StepVenues } from './step-venues'
import { StepsIndicators } from './steps-indicators'
import { ValidateBtn } from './validate-btn'

const CocoonTypeFooter = ({ activeStepId }: { activeStepId: StepId }) => {
	const activeStep = useMemo(() => {
		if (activeStepId === 'venues') return 1
		if (activeStepId === 'spaces') return 2
		if (activeStepId === 'priority') return 3
		return 1
	}, [activeStepId])

	return (
		<div className='flex flex-col gap-4 border-t px-6 py-4'>
			<StepsIndicators
				activeStep={activeStep}
				totalSteps={3}
			/>

			<div className='flex items-center gap-4'>
				<PrevBtn />
				{activeStepId === 'priority' ? <ValidateBtn /> : <NextBtn />}
			</div>
		</div>
	)
}

export const AddVenueAndCocoonProviders = ({
	value,
	initialStepId,
	onChange,
}: PropsWithChildren<{
	value?: LocationSchema
	initialStepId?:
		| 'locationType'
		| 'venues'
		| 'spaces'
		| 'priority'
		| 'calendarSync'
	onChange: (data: LocationSchema) => void
}>) => {
	const form = useForm<LocationSchema>({
		resolver: zodResolver(locationSchema),
		defaultValues: {
			type: value?.type,
			// @ts-expect-error - union error
			venue_id: value?.venue_id ?? undefined,
			// @ts-expect-error - union error
			space_ids: value?.space_ids ?? [],
		},
	})

	const formValues = form.watch()
	const isCocoon = formValues.type === 'cocoon'
	const isRemote = formValues.type === 'remote'
	const venueId = isCocoon ? formValues.venue_id : undefined
	const spaceIds = isCocoon ? formValues.space_ids : []

	const onSubmit = (formValues: LocationSchema) => {
		if (isCocoon) {
			onChange({
				type: 'cocoon',
				// @ts-expect-error - union error
				venue_id: formValues.venue_id,
				// @ts-expect-error - union error
				space_ids: formValues.space_ids,
			})
		}

		if (isRemote) {
			onChange({ type: 'remote' })
		}
	}

	return (
		<StepProvider
			steps={
				[
					'locationType',
					'venues',
					'spaces',
					'priority',
					'calendarSync',
				] as const
			}
			initialStepId={initialStepId ?? 'locationType'}
			getPreviousStepId={({ activeStepId }) => {
				if (activeStepId === 'calendarSync') return 'locationType'
				if (activeStepId === 'venues') return 'locationType'
				if (activeStepId === 'spaces') return 'venues'
				if (activeStepId === 'priority') return 'spaces'
				return null
			}}
			getNextStepId={({ activeStepId }) => {
				if (activeStepId === 'locationType') {
					if (isCocoon) return 'venues'
					if (isRemote) return 'calendarSync'
					return null
				}

				if (activeStepId === 'venues') return venueId ? 'spaces' : null

				if (activeStepId === 'spaces') {
					return spaceIds.length ? 'priority' : null
				}

				return null
			}}
			canNext={({ activeStepId }) => {
				if (activeStepId === 'locationType') return !!formValues.type
				if (activeStepId === 'venues') return !!venueId
				if (activeStepId === 'spaces') return !!spaceIds.length
				return false
			}}
			renderStep={({ activeStepId }) => (
				<Form {...form}>
					<form
						className='flex w-full flex-1 flex-col overflow-hidden'
						onSubmit={(e) => {
							e.preventDefault()
							e.stopPropagation()
							void form.handleSubmit(onSubmit)(e)
						}}
					>
						<div className='flex min-h-[250px] flex-1 flex-col overflow-hidden'>
							{activeStepId === 'locationType' && <StepLocationType />}
							{activeStepId === 'venues' && <StepVenues />}
							{activeStepId === 'spaces' && <StepSpaces />}
							{activeStepId === 'priority' && <StepPriority />}
							{activeStepId === 'calendarSync' && <StepCalendarSync />}

							{activeStepId !== 'locationType' && isCocoon && (
								<CocoonTypeFooter activeStepId={activeStepId} />
							)}
						</div>
					</form>
				</Form>
			)}
		/>
	)
}

export const AddVenueAndCocoon = ({
	value,
	children,
	initialStepId,
	onChange,
}: PropsWithChildren<{
	value?: LocationSchema
	initialStepId?:
		| 'locationType'
		| 'venues'
		| 'spaces'
		| 'priority'
		| 'calendarSync'
	onChange: (data: LocationSchema) => void
}>) => {
	const [open, setOpen] = useState(false)

	return (
		<Dialog
			open={open}
			onOpenChange={setOpen}
		>
			<DialogTrigger asChild>{children}</DialogTrigger>

			<DialogContent className='flex max-h-[80vh] max-w-xl flex-col gap-0 overflow-hidden p-0'>
				<AddVenueAndCocoonProviders
					value={value}
					initialStepId={initialStepId}
					onChange={(data) => {
						onChange(data)
						setOpen(false)
					}}
				/>
			</DialogContent>
		</Dialog>
	)
}
